import React from 'react';
import { RHP } from 'providers';
import { useConversionOptions } from 'hooks';
import { useForm } from 'components/form';
import * as Alert from 'components/alert';
import * as Form from 'components/form';
import * as Page from 'components/page';

export function StartupStep2(props) {
    const conv = useConversionOptions(['investmentOrRevenues'], { namespace: 'currency' });
    const { vars, putStartupRankingApplication, putStartupDetails } = props.useRankingStartup;
    const { startupDetails, ranking } = vars;
    const pastYear = new Date().getFullYear() - 1;
    if (!startupDetails.rankingData) {
        startupDetails.rankingData = {};
    }

    if (!startupDetails.rankingData?.[ranking.id]) {
        startupDetails.rankingData[ranking.id] = {
            revenuesRange: [],
            investmentRange: [],
            categories: [],
        };
    }
    // Initialize a new form handler
    const form = useForm({
        ...vars.application,
        startupName: startupDetails.name,
        cnpj: startupDetails.demographics.status.cnpj,
        isControlledByCorp: startupDetails.isControlledByCorp,
        controllingCorpName: startupDetails.controllingCorpName,
        shortDescription: startupDetails.description,
        revenuesRange: startupDetails.rankingData[ranking.id]?.revenuesRange,
        investmentRange: startupDetails.rankingData[ranking.id]?.investmentRange,
        isUpToDate: !!vars.application.appliedAt,
    });

    // Check for previous step conclusion
    if (!vars.application.steps || vars.application.steps.step1 !== true) {
        RHP.replace('/ranking/latam/startup/');
        return null;
    }
    // Handle form submitions
    const onSubmit = values => {
        const rankingPayload = {
            ...vars.application,
            ...values,
            investmentRange: values.investmentRange,
            revenuesRange: values.revenuesRange,
        };
        // Set the current step to true;
        if (!rankingPayload.steps) rankingPayload.steps = {};
        rankingPayload.steps.step2 = true;

        const startupPayload = {
            description: values.shortDescription,
            name: values.startupName,
            demographics: {
                status: {
                    cnpj: values.cnpj,
                },
            },
            isControlledByCorp: values.isControlledByCorp,
            controllingCorpName: values.controllingCorpName,
            language: startupDetails.language,
        };

        return putStartupDetails(startupPayload).then(
            putStartupRankingApplication(rankingPayload)
                .then(() => RHP.push('/ranking/latam/startup/step3'))
                .catch(() => form.submited())
        );
    };

    const shortDescriptionLabel = {
        pt: `${props.t('glossary:shortDescription')} ${props.t('glossary:inPortuguese')}`,
        en: `${props.t('glossary:shortDescription')} ${props.t('glossary:inEnglish')}`,
        es: `${props.t('glossary:shortDescription')} ${props.t('glossary:inSpanish')}`,
    };

    const fields = {
        startupName: {
            error: form.errors.startupName,
            label: props.t('glossary:startupName'),
            name: 'startupName',
            type: 'generic',
            value: form.values.startupName,
            onChange: form.handleChange,
        },
        cnpj: {
            error: form.errors.cnpj,
            label: props.t('glossary:cnpj'),
            name: 'cnpj',
            type: 'generic',
            value: form.values.cnpj,
            onChange: form.handleChange,
            required: false,
        },
        isControlledByCorp: {
            error: form.errors.isControlledByCorp,
            label: `${props.t('StartupStep2.isControlledByCorp')} `,
            name: 'isControlledByCorp',
            required: false,
            type: 'checkbox',
            value: form.values.isControlledByCorp,
            onChange: form.handleChange,
        },
        controllingCorpName: {
            error: form.errors.controllingCorpName,
            label: props.t('StartupStep2.controllerCorpName'),
            name: 'controllingCorpName',
            type: 'generic',
            required: form.values.isControlledByCorp,
            value: form.values.controllingCorpName,
            onChange: form.handleChange,
        },
        shortDescription: {
            error: form.errors.shortDescription,
            label: shortDescriptionLabel[startupDetails.language],
            name: 'shortDescription',
            type: 'textarea',
            value: form.values.shortDescription,
            onChange: form.handleChange,
        },

        revenuesRange: {
            error: form.errors.revenuesRange,
            label: `${props.t('glossary:revenuesRange')}`,
            name: 'revenuesRange',
            type: 'SelectRangeArray',
            options: [
                { name: props.t('glossary:selectOption'), value: '' },
                ...conv.investmentOrRevenues,
            ],
            value: form.values.revenuesRange,
            onChange: form.handleChange,
        },
        investmentRange: {
            error: form.errors.investmentRange,
            label: `${props.t('glossary:investmentRange')} 31/12/${pastYear}`,
            name: 'investmentRange',
            type: 'SelectRangeArray',
            options: [
                { name: props.t('glossary:selectOption'), value: '' },
                ...conv.investmentOrRevenues,
            ],
            value: form.values.investmentRange,
            onChange: form.handleChange,
        },
    };

    const propsScaleUpWarning = {
        descr: '',
        icons: 'far fa-info-circle',
        type: 'warning',
    };
    let scaleUpValueWarning = false;
    if (fields.revenuesRange.value[0] >= 2500000 || fields.investmentRange.value[0] >= 2500000) {
        scaleUpValueWarning = true;
        propsScaleUpWarning.descr = props.t('StartupStep2.scaleUpWarning', {
            startupId: startupDetails._id,
        });
    }

    if (
        (fields.investmentRange.value[0] == 0 && fields.investmentRange.value[1] >= 20000000) ||
        (fields.revenuesRange.value[0] == 0 && fields.revenuesRange.value[1] >= 20000000)
    ) {
        scaleUpValueWarning = true;
        propsScaleUpWarning.bgColor = '#f44336';
        propsScaleUpWarning.color = 'white';
        propsScaleUpWarning.descr = props.t('StartupStep2.openCorpWarning');
    }

    const propsNotEligibleForRanking = {
        descr: props.t('StartupStep2.notEligibleForRanking'),
        icons: 'far fa-info-circle',
        type: 'warning',
    };
    const evSubmit = ev => form.handleSubmit(ev, onSubmit);

    return (
        <Page.Page
            name="StartupStep2"
            back="/ranking/latam/startup"
            customBackText={props.t('glossary:backToRanking')}
        >
            <Page.Title>{props.t('StartupStep2.title')}</Page.Title>
            <Page.Descr>{props.t('StartupStep2.descr', { rankingYear: ranking.year })}</Page.Descr>

            <Form.Form name="StartupStep2Form" onSubmit={evSubmit}>
                <Form.Field {...fields.startupName} />
                <Form.Field {...fields.cnpj} />
                <Form.Field {...fields.isControlledByCorp} />
                {fields.isControlledByCorp.value && <Form.Field {...fields.controllingCorpName} />}
                {fields.isControlledByCorp.value && (
                    <Alert.AlertRow {...propsNotEligibleForRanking} />
                )}
                <Form.Field {...fields.shortDescription} />
                <Form.Field {...fields.revenuesRange} />
                <Form.Field {...fields.investmentRange} />

                {scaleUpValueWarning && <Alert.AlertRow {...propsScaleUpWarning} />}
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Page.Buttn style={{ width: '25%' }} to="/ranking/latam/startup/step1">
                        <span className="icon far fa-long-arrow-left" aria-hidden="true" />
                        <span className="text" style={{ margin: '0 0 0 auto' }}>
                            {props.t('glossary:back')}
                        </span>
                    </Page.Buttn>
                    <div style={{ width: '80%' }}>
                        <Form.Submt paddingTop="10rem" {...form} text={props.t('glossary:save')} />
                    </div>
                </div>
            </Form.Form>
        </Page.Page>
    );
}
export default StartupStep2;
